// src/components/FoodProductDetailScreen.jsx
import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useFoodCart } from '../store/foodCartState';
import { formatCurrency } from '../utils/helpers';
import {
  FiArrowRight, FiShoppingCart, FiStar, FiPackage, FiMinus, FiPlus,
  FiCheck, FiCheckCircle
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import MediaCarousel, { buildMediaList } from './MediaCarousel';

const FoodProductDetailScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const cart = useFoodCart();

  const { service, provider } = location.state || {};
  const [qty, setQty] = useState(1);

  // fallback إذا فُتحت الصفحة مباشرة بدون state (مثلاً بعد تحديث الصفحة)
  if (!service || !provider) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6 text-center" dir="rtl">
        <FiPackage size={40} className="text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm mb-4">لا يمكن عرض الوجبة مباشرة، يرجى الرجوع لخدمة فود</p>
        <button
          onClick={() => navigate('/food-store')}
          className="bg-red-600 text-white px-5 py-2 rounded-lg font-bold text-sm"
        >
          العودة لخدمة فود
        </button>
      </div>
    );
  }

  const stock = service.stock ?? 0;
  const inStock = stock > 0;
  const inCart = cart.items.some((e) => e.service.id === service.id);

  const addToCart = () => {
    for (let i = 0; i < qty; i++) cart.addItem(service, provider);
    toast.success('تمت الإضافة إلى السلة', { duration: 2000 });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-28" dir="rtl">
      {/* صورة + هيدر */}
      <div className="relative bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center overflow-hidden h-64 sm:h-80 border-b border-gray-150">
        <MediaCarousel
          media={buildMediaList(service)}
          title={service.name}
          height="h-64 sm:h-80"
          objectFit="object-contain"
        />
        <div className="absolute top-0 inset-x-0 flex items-center justify-between p-3 z-10">
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-900/60 hover:bg-gray-900/80 backdrop-blur-sm p-2 rounded-xl text-white transition flex items-center justify-center"
          >
            <FiArrowRight size={18} />
          </button>
          <button
            onClick={() => navigate('/food-store/cart')}
            className="relative bg-gray-900/60 hover:bg-gray-900/80 backdrop-blur-sm p-2 rounded-xl text-white transition flex items-center justify-center"
          >
            <FiShoppingCart size={18} />
            {cart.totalCount > 0 && (
              <span className="absolute -top-1 -left-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                {cart.totalCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="p-5 -mt-6 relative">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h1 className="text-xl font-bold text-gray-900">{service.name}</h1>
            {service.categoryName && (
              <span className="flex-shrink-0 bg-gray-100 text-gray-600 text-[11px] font-bold px-3 py-1 rounded-full">
                {service.categoryName}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 mb-4 text-sm">
            <div className="flex items-center gap-1">
              <FiStar className="text-yellow-400" />
              <span className="text-gray-600">
                {(service.averageRating || 0) > 0 ? service.averageRating.toFixed(1) : 'لا يوجد تقييم بعد'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <FiPackage className="text-gray-400" size={14} />
              <span className={inStock ? 'text-gray-500' : 'text-red-500'}>
                {inStock ? `المخزون: ${stock}` : 'نفذت الكمية'}
              </span>
            </div>
          </div>

          {/* السعر + الكمية */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] text-gray-400 mb-1">السعر</p>
              <p className="text-2xl font-black text-gray-900">
                {formatCurrency ? formatCurrency(service.price || 0) : `${service.price} د.ج`}
              </p>
            </div>
            {inStock && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => qty > 1 && setQty(qty - 1)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    qty > 1 ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  <FiMinus size={14} />
                </button>
                <span className="font-bold text-gray-900 w-5 text-center">{qty}</span>
                <button
                  onClick={() => qty < stock && setQty(qty + 1)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    qty < stock ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  <FiPlus size={14} />
                </button>
              </div>
            )}
          </div>

          {/* بطاقة المزود */}
          <div
            onClick={() => navigate(`/provider-public/${provider.id}`)}
            className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl p-3.5 cursor-pointer hover:shadow-sm transition mb-4"
          >
            <img
              src={provider.avatarUrl || 'https://via.placeholder.com/100'}
              alt=""
              className="w-11 h-11 rounded-full object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-sm font-bold text-gray-900 truncate">{provider.fullName}</p>
                {provider.isVerified && <FiCheckCircle className="text-blue-500" size={13} />}
              </div>
              <p className="text-xs text-gray-400 truncate">
                {provider.stateName} • {provider.professionName}
              </p>
            </div>
            <span className="text-[11px] font-bold text-gray-900 bg-gray-100 px-3 py-1.5 rounded-lg whitespace-nowrap">
              زيارة المتجر
            </span>
          </div>

          {/* الوصف */}
          {service.description && (
            <div>
              <p className="text-sm font-bold text-gray-800 mb-2">وصف الوجبة</p>
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-600 leading-relaxed">
                {service.description}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* شريط سفلي ثابت */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 p-4 flex items-center gap-4 z-30">
        <div>
          <p className="text-[11px] text-gray-400">الإجمالي</p>
          <p className="text-lg font-black text-gray-900">
            {formatCurrency ? formatCurrency((service.price || 0) * qty) : `${(service.price || 0) * qty} د.ج`}
          </p>
        </div>
        <button
          onClick={inStock ? addToCart : undefined}
          disabled={!inStock}
          className={`flex-1 py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition ${
            !inStock
              ? 'bg-gray-200 text-gray-400'
              : inCart
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          {inCart ? <FiCheck /> : <FiShoppingCart />}
          {!inStock ? 'نفذت الكمية' : inCart ? 'أضف المزيد للسلة' : 'أضف إلى السلة'}
        </button>
      </div>
    </div>
  );
};

export default FoodProductDetailScreen;
